import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Plus, ChevronRight, MapPin, Trash2 } from 'lucide-react';

export default function RotasLista() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [rotas, setRotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadRotas = () => {
    setLoading(true);
    api.get('/rotas').then(r => setRotas(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { loadRotas(); }, []);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/rotas/${deleteTarget.id}`);
      setDeleteTarget(null);
      loadRotas();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao inativar rota');
    } finally { setDeleting(false); }
  };

  return (
    <div data-testid="rotas-lista">
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="confirm-delete-modal">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-dashboard font-bold text-lg text-brand-text mb-2">Confirmar inativação</h3>
            <p className="text-sm font-body text-brand-text-muted mb-5">
              Inativar a rota <strong className="text-brand-text">"{deleteTarget.label}"</strong>? Os clientes vinculados perderão a rota de entrega.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2 rounded-lg border border-brand-border text-sm font-dashboard text-brand-text-muted hover:bg-brand-bg" data-testid="cancel-delete-btn">Cancelar</button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2 rounded-lg bg-red-600 text-white text-sm font-dashboard font-semibold hover:bg-red-700 disabled:opacity-60" data-testid="confirm-delete-btn">{deleting ? 'Inativando...' : 'Inativar'}</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div><h1 className="dashboard-heading text-2xl">Rotas</h1><p className="text-brand-text-muted font-body text-sm">{rotas.length} rota(s)</p></div>
        <Link to="/admin/rotas/nova" className="flex items-center gap-2 py-2 px-4 rounded-lg btn-primary font-dashboard font-semibold text-sm" data-testid="new-rota-btn"><Plus className="w-4 h-4" /> Nova Rota</Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? <div className="col-span-3 flex justify-center py-12"><div className="w-6 h-6 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" /></div> :
          rotas.length === 0 ? <p className="col-span-3 text-center text-brand-text-muted text-sm py-12 font-body">Nenhuma rota cadastrada</p> :
          rotas.map(r => (
            <div key={r.id} className="card-dashboard" data-testid="rota-card">
              <div className="flex items-start justify-between mb-2">
                <div className="w-9 h-9 bg-brand-accent-light rounded-lg flex items-center justify-center"><MapPin className="w-4 h-4 text-brand-primary" /></div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${r.is_ativo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{r.is_ativo ? 'Ativa' : 'Inativa'}</span>
              </div>
              <h3 className="font-dashboard font-bold text-brand-text mt-2">{r.nome}</h3>
              <p className="text-xs text-brand-text-muted font-body mt-0.5">{r.forma_entrega} · {r.turnos?.join(' / ')}</p>
              <p className="text-xs text-brand-text-muted font-body mt-0.5">{r.total_clientes || 0} cliente(s)</p>
              {r.entregador_nome && <p className="text-xs text-brand-text-muted font-body mt-0.5">Entregador: {r.entregador_nome}</p>}
              <div className="flex items-center justify-between mt-3">
                <Link to={`/admin/rotas/${r.id}`} className="flex items-center gap-1 text-brand-primary text-xs font-dashboard font-semibold hover:underline"><span>Editar</span><ChevronRight className="w-3 h-3" /></Link>
                {isAdmin && r.is_ativo && (
                  <button
                    onClick={() => setDeleteTarget({ id: r.id, label: r.nome })}
                    className="text-red-400 hover:text-red-600 transition-colors"
                    title="Inativar rota"
                    data-testid="delete-rota-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}
